import React, { useState } from 'react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import {
  Code,
  Cog,
  Sparkles,
  Briefcase,
  Gamepad2,
  ArrowRight,
  Zap,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import ProgrammerGame from '../components/Profession/ProgrammerGame';
import MechatronicsGame from '../components/Profession/MechatronicsGame';
import MechatronicsVocabularyGame from '../components/Profession/MechatronicsVocabularyGame';
import ProgrammerSyntaxGame from '../components/Profession/ProgrammerSyntaxGame';
import SpeedMatchingGame from '../components/Profession/SpeedMatchingGame';
import TechnicalKnowledgeBase from '../components/Profession/TechnicalKnowledgeBase';
import WorkplaceScenarios from '../components/Profession/WorkplaceScenarios';

const PROFESSIONS = [
  {
    id: 'programmer',
    title: 'Programues Softueri',
    germanTitle: 'Softwareentwickler / Informatiker',
    description: 'Zotëroni gjermanishten teknike për Frontend, Backend, APIs, DevOps, Git dhe mbledhjet Scrum / Daily Standups.',
    icon: Code,
    badge: 'IT & Software',
    accentColor: '#DD0000',
  },
  {
    id: 'mechatronics',
    title: 'Inxhinier Mekatronik',
    germanTitle: 'Mechatronik-Ingenieur / Automatisierung',
    description: 'Përqendrohuni te sistemet PLC/SPS, skemat elektrike (Schaltpläne), pneumatika, sensorët dhe mirëmbajtja industriale.',
    icon: Cog,
    badge: 'Inxhinieri & Industri 4.0',
    accentColor: '#FFCE00',
  },
];

const TABS = [
  { id: 'lexicon', label: 'Fjalori Teknik', icon: Code },
  { id: 'scenarios', label: 'Situata në Punë & Dialogje', icon: Briefcase },
  { id: 'games', label: 'Sfidat & Lojërat Praktike', icon: Gamepad2 },
];

const ProfessionChoicePage = () => {
  const { user } = useAuth();
  const [selectedProfession, setSelectedProfession] = useState(user?.profession || 'programmer');
  const [activeTab, setActiveTab] = useState('lexicon');
  const [showGame, setShowGame] = useState(false);
  const [gameMode, setGameMode] = useState(null); // 'sprint', 'fun', 'blitz'

  const handleSelectProfession = async (prof) => {
    setSelectedProfession(prof);
    setShowGame(false);
    try {
      await authAPI.updateProfile({ profession: prof });
    } catch (error) {
      console.error('Failed to save profession:', error);
    }
  };

  const activeProfConfig = PROFESSIONS.find((p) => p.id === selectedProfession) || PROFESSIONS[0];

  // If playing a game
  if (showGame) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-[1440px] space-y-6 px-1 pb-10 font-body sm:px-3">
          <div className="mb-6 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setShowGame(false)}
              className="flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3.5 py-2 text-xs font-bold text-zinc-600 shadow-sm transition hover:border-[#dd0000] hover:text-[#dd0000]"
            >
              ← Kthehu te Qendra Profesionale
            </button>
            <span className="text-xs font-extrabold text-zinc-400 uppercase tracking-wider">
              {activeProfConfig.title} • {gameMode?.toUpperCase()}
            </span>
          </div>

          {selectedProfession === 'programmer' ? (
            gameMode === 'sprint' ? (
              <ProgrammerGame />
            ) : gameMode === 'blitz' ? (
              <SpeedMatchingGame />
            ) : (
              <ProgrammerSyntaxGame />
            )
          ) : gameMode === 'sprint' ? (
            <MechatronicsGame />
          ) : gameMode === 'blitz' ? (
            <SpeedMatchingGame />
          ) : (
            <MechatronicsVocabularyGame />
          )}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-1 pb-10 font-body sm:px-3">
        <header className="overflow-hidden rounded-2xl border border-[#e3e3e3] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.05)]">
          <div aria-hidden="true" className="flex h-1.5">
            <span className="flex-1 bg-zinc-950" />
            <span className="flex-1 bg-[#dd0000]" />
            <span className="flex-1 bg-[#ffce00]" />
          </div>
          <div className="grid gap-6 p-5 sm:p-7 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
            <div className="max-w-2xl">
              <p className="mb-2 flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-[#dd0000]">
                <Sparkles size={14} /> Moduli profesional · Gjermanisht B1/B2/C1
              </p>
              <h1 className="text-2xl font-extrabold tracking-tight text-[#171717] sm:text-3xl">
                {activeProfConfig.title}
              </h1>
              <p className="mt-1 text-sm font-semibold text-zinc-500">{activeProfConfig.germanTitle}</p>
              <p className="mt-3 text-sm leading-6 text-zinc-600">{activeProfConfig.description}</p>
            </div>

            <div className="min-w-0 rounded-2xl border border-[#e9e4dc] bg-[#fbfaf7] p-3">
              <p className="mb-2 px-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-zinc-500">Zgjidh profesionin</p>
              <div className="grid grid-cols-2 gap-2">
                {PROFESSIONS.map((profession) => {
                  const Icon = profession.icon;
                  const isCurrent = selectedProfession === profession.id;
                  return (
                    <button
                      key={profession.id}
                      type="button"
                      onClick={() => handleSelectProfession(profession.id)}
                      aria-pressed={isCurrent}
                      className={`flex min-h-11 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold transition ${
                        isCurrent
                          ? 'border-zinc-950 bg-zinc-950 text-[#ffce00] shadow-sm'
                          : 'border-zinc-200 bg-white text-zinc-600 hover:border-[#dd0000] hover:text-[#dd0000]'
                      }`}
                    >
                      <Icon size={15} />
                      <span className="whitespace-nowrap">{profession.id === 'programmer' ? 'Programues' : 'Mekatronik'}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </header>

        {/* Feature Navigation Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto border-b border-zinc-200 pb-2">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-bold transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-zinc-950 text-[#FFCE00] shadow-sm'
                    : 'border border-zinc-200 bg-white text-zinc-600 hover:border-[#dd0000] hover:text-[#dd0000]'
                }`}
              >
                <Icon size={15} />
                <span>{tab.label}</span>
                {tab.badge && (
                  <span className="rounded-full bg-[#FFCE00]/20 px-1.5 py-0.2 text-[9px] font-extrabold text-[#FFCE00] border border-[#FFCE00]/40">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Technical Lexicon */}
        {activeTab === 'lexicon' && (
          <TechnicalKnowledgeBase profession={selectedProfession} />
        )}

        {/* Tab 3: Workplace Scenarios */}
        {activeTab === 'scenarios' && (
          <WorkplaceScenarios profession={selectedProfession} />
        )}

        {/* Tab 4: Interactive Practice Games */}
        {activeTab === 'games' && (
          <div className="space-y-6">
            <div className="text-center max-w-lg mx-auto">
              <h3 className="text-xl font-extrabold text-zinc-950 tracking-tight">
                Zgjidhni Sfidën e Trajnimit
              </h3>
              <p className="mt-1 text-xs text-zinc-500">
                Fitoni XP dhe përforconi fjalorin teknik përmes kuizeve interaktive dhe sfidave me kohë.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Technical Sprint */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  setGameMode('sprint');
                  setShowGame(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setGameMode('sprint');
                    setShowGame(true);
                  }
                }}
                className="group relative flex flex-col justify-between rounded-2xl border border-zinc-200 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-[#dd0000] hover:shadow-lg cursor-pointer"
              >
                <div>
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff0f0] text-[#dd0000] transition-colors group-hover:bg-zinc-950 group-hover:text-[#FFCE00]">
                    <Code className="h-6 w-6" />
                  </div>
                  <h4 className="text-base font-extrabold text-zinc-950">Technical Sprint</h4>
                  <p className="mt-1 text-xs text-zinc-500">
                    Mësoni teorinë dhe gramatikën e saktë teknike përmes shembujve realë të kodit dhe komponentëve.
                  </p>
                </div>
                <div className="mt-6 flex items-center justify-between rounded-xl border-t border-zinc-100 px-1 pt-4 text-xs font-bold text-[#dd0000] transition-colors">
                  <span>Luaj tani</span>
                  <ArrowRight size={14} />
                </div>
              </div>

              {/* Skill Game */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  setGameMode('fun');
                  setShowGame(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setGameMode('fun');
                    setShowGame(true);
                  }
                }}
                className="group relative flex flex-col justify-between rounded-2xl border border-zinc-200 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-[#dd0000] hover:shadow-lg cursor-pointer"
              >
                <div>
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff8d9] text-[#8b7000] transition-colors group-hover:bg-zinc-950 group-hover:text-[#FFCE00]">
                    <BookOpen className="h-6 w-6" />
                  </div>
                  <h4 className="text-base font-extrabold text-zinc-950">Skill & Syntax Game</h4>
                  <p className="mt-1 text-xs text-zinc-500">
                    Mbajtja mend e fjalorit teknik dhe sintaksës përkatëse në gjuhën gjermane.
                  </p>
                </div>
                <div className="mt-6 flex items-center justify-between rounded-xl border-t border-zinc-100 px-1 pt-4 text-xs font-bold text-[#dd0000] transition-colors">
                  <span>Luaj tani</span>
                  <ArrowRight size={14} />
                </div>
              </div>

              {/* Blitz Match */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  setGameMode('blitz');
                  setShowGame(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setGameMode('blitz');
                    setShowGame(true);
                  }
                }}
                className="group relative flex flex-col justify-between rounded-2xl border border-zinc-200 bg-white p-6 text-zinc-950 transition-all hover:-translate-y-0.5 hover:border-[#dd0000] hover:shadow-lg cursor-pointer"
              >
                <div>
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-950 text-[#FFCE00] transition-colors group-hover:bg-[#FFCE00] group-hover:text-zinc-950">
                    <Zap className="h-6 w-6" />
                  </div>
                  <div className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider text-[#FFCE00] mb-1">
                    <span>Shpejtësi dhe reflekse</span>
                  </div>
                  <h4 className="text-base font-extrabold text-zinc-950">Blitz Match</h4>
                  <p className="mt-1 text-xs text-zinc-500">
                    Përputhni termat teknikë me kuptimin e tyre shqip me shpejtësi të lartë para se të mbarojë koha.
                  </p>
                </div>
                <div className="mt-6 flex items-center justify-between rounded-xl border-t border-zinc-100 px-1 pt-4 text-xs font-bold text-[#dd0000] transition-colors">
                  <span>Fillo sfidën Blitz</span>
                  <ArrowRight size={14} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProfessionChoicePage;
